import React from 'react';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import { Verification } from 'api/verifications/types';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { getFieldKey } from 'utils';
import { validatePreviousDriverLicense } from 'lib/validations/driverLicenseValidations';
import { VERIFICATION_FORM_NAME } from '../../constants';
import DrivingExperienceEnhanced from './DrivingExperienceEnhanced';

export interface DrivingExperienceProps {
  verification: Verification;
}

type Props = DrivingExperienceProps &
  InjectedFormProps<{}, DrivingExperienceProps>;

const DrivingExperience = ({ verification }: Props) => (
  <div className='dee-enhanced-page'>
    <T
      value='components.Report.Verification.MatrixVerification.DrivingExperience.enhanced.heading'
      tag='h2'
      className='mastodon-heading1'
    />
    <p className='mastodon-paragraph'>
      {i18n.renderHTML(
        'components.Report.Verification.MatrixVerification.DrivingExperience.enhanced.subheading',
        { company: verification.company },
      )}
    </p>
    <DrivingExperienceEnhanced verification={verification} />
    <hr className='dee-enhanced-page__footer-rule' />
  </div>
);

const validate = (
  values: Record<string, any>,
  props: DrivingExperienceProps,
) => {
  const licenseField = props.verification?.fieldConfigurations?.find(
    f => f.type === 'driver_license_info',
  );
  if (!licenseField) return {};

  const key = getFieldKey(licenseField);
  const dl = values?.[key] || {};
  const fieldErrors = validatePreviousDriverLicense({
    dlState: dl.dl_state,
    dlNumber: dl.dl_number,
    alreadyRunStates: (licenseField.licensesOnFile ?? []).map(
      license => license.state,
    ),
  });

  const nested: { dl_state?: string; dl_number?: string } = {};
  if (fieldErrors.dlState) nested.dl_state = fieldErrors.dlState;
  if (fieldErrors.dlNumber) nested.dl_number = fieldErrors.dlNumber;

  return Object.keys(nested).length ? { [key]: nested } : {};
};

const DrivingExperienceForm = reduxForm<{}, DrivingExperienceProps>({
  form: VERIFICATION_FORM_NAME,
  validate,
})(DrivingExperience);

export default i18n.renderTranslation()(DrivingExperienceForm);
